/**
 * Sidebar moderne fixe. Navigation principale + section "Autres outils HSE"
 * (liens externes vers d'autres applications Lafarge/Holcim), sans casser
 * l'architecture actuelle.
 */
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, AlertTriangle, ClipboardCheck, ShieldCheck, FileCheck2,
  Activity, Trophy, HardHat, ClipboardList, Truck,
  TrendingUp, FolderOpen, ListChecks, ChevronLeft, ChevronRight, Gauge,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SIDEBAR } from "@/constants/testIds";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: SIDEBAR.navDashboard },
  { to: "/score-bog", label: "Score BoG 2026", icon: Gauge, testId: SIDEBAR.navScoreBog },
  { to: "/findings", label: "Findings", icon: AlertTriangle, testId: SIDEBAR.navFindings },
  { to: "/actions", label: "Actions Icare", icon: ClipboardCheck, testId: SIDEBAR.navActions },
  { to: "/ccv", label: "CCV", icon: ShieldCheck, testId: SIDEBAR.navCcv },
  { to: "/pced", label: "PCED", icon: FileCheck2, testId: SIDEBAR.navPced },
  { to: "/accidents", label: "Accidentologie", icon: HardHat, testId: SIDEBAR.navAccidents },
  { to: "/indicateurs", label: "Indicateurs", icon: Activity, testId: SIDEBAR.navIndicateurs },
  { to: "/classements", label: "Classements", icon: Trophy, testId: SIDEBAR.navClassements },
];

// Liens vers d'autres applications HSE Lafarge/Holcim (hors de ce Dashboard,
// ouverts dans un nouvel onglet).
const EXTERNAL_LINKS = [
  { label: "Autorisation de Travail", icon: ClipboardList, href: "https://adt.gds-ciments-france.fr/" },
  { label: "Training Check", image: "/training-check-icon.png", href: "https://formations.gds-ciments-france.fr/" },
  { label: "Modes Opératoires", image: "/modes-operatoires-icon.png", href: "https://sop.gds-ciments-france.fr/" },
  { label: "Inspections engins", icon: Truck, href: "https://checklistes.gds-ciments-france.fr/" },
  { label: "BoG Pro", icon: TrendingUp, href: "https://bogpro.web.app/dashboard" },
  { label: "GED HSE France", icon: FolderOpen, href: "https://connect.holcim.com/reseaux-hse-france/" },
  { label: "ICcare", icon: ListChecks, href: "https://eu3.ehssrisk.com/holcim/#/main" },
  { label: "Management of Change", image: "/moc-icon.png", href: "https://moc.gds-ciments-france.fr/" },
  { label: "Energie Isolation Assessment", image: "/energy-isolation-icon.png", href: "https://worldclass-ei.gds-ciments-france.fr/" },
  { label: "Inspection des fronts", image: "/fronts-carriere-icon.png", href: "https://fronts-carriere.gds-ciments-france.fr/" },
  { label: "Protocoles Sécurité", image: "/protocoles-icon.png", href: "https://protocoles.gds-ciments-france.fr/" },
  { label: "Analyse HFACS", image: "/hfacs-icon.png", href: "http://hfacs.gds-ciments-france.fr/" },
  { label: "Échafaudages", image: "/scaffold-check-icon.png", href: "https://scaffold-check-2.emergent.host/?utm_source=share" },
  { label: "Bon d'isolation Énergétique", image: "/bon-isolation-icon.png", href: "https://bon-isolation-ciment.emergent.host/" },
];

// Génère un slug lisible pour data-testid, en normalisant les accents
// (ex: "Vérification..." -> "verification-...", pas "v-rification-...").
function slugify(label) {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function Sidebar({ collapsed, onToggle }) {
  return (
    <aside
      data-testid={SIDEBAR.container}
      className={cn(
        "fixed left-0 top-0 z-30 flex h-screen flex-col bg-[#0B5394] text-white transition-all duration-200",
        collapsed ? "w-20" : "w-64"
      )}
    >
      <div className="flex h-16 items-center gap-3 border-b border-white/10 px-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white p-1.5">
          <img src="/lafarge-mark.png" alt="Lafarge" className="h-full w-full object-contain" />
        </div>
        {!collapsed && (
          <span className="truncate text-sm font-bold leading-tight">
            Dashboard Sécurité
            <br />
            <span className="text-xs font-normal text-white/70">Direction Lafarge</span>
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testId}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                isActive ? "bg-white text-[#0B5394] shadow-sm" : "text-white/85 hover:bg-white/10"
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <p className="px-3 pb-1 pt-6 text-[10px] font-semibold uppercase tracking-wider text-white/40">
            Autres outils HSE
          </p>
        )}
        {EXTERNAL_LINKS.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`sidebar-external-${slugify(link.label)}`}
            title={`Ouvrir ${link.label} dans un nouvel onglet`}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/85 transition-all duration-200 hover:bg-white/10"
          >
            {link.image ? (
              <img src={link.image} alt="" className="h-5 w-5 shrink-0 rounded-sm object-cover" />
            ) : (
              <link.icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            )}
            {!collapsed && <span className="min-w-0 flex-1 truncate">{link.label}</span>}
            {!collapsed && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-white/40" strokeWidth={1.5} />}
          </a>
        ))}
      </nav>

      <button
        data-testid={SIDEBAR.collapseToggle}
        onClick={onToggle}
        className="flex h-12 items-center justify-center border-t border-white/10 text-white/70 transition-colors duration-200 hover:bg-white/10"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}
